"use strict";
/**
 * Client Permissions — single source of truth for UI gating.
 *
 * The server sends a `Permissions` object on login and session-restore
 * (see `src/server/auth/levels.ts` → `serializePermissions`). It is stored
 * verbatim on `AppState.permissions`. This module is the ONLY place the rest
 * of the client reads it from, so every gate asks the same questions the same
 * way and the server contract has one client-side mirror.
 *
 * IMPORTANT: gating here is cosmetic. The server independently enforces every
 * cap and feature on the order paths and can never be relaxed from the browser
 * (see PERMISSION_LEVELS_PLAN.md). These helpers only stop doomed clicks and
 * hide controls the operator can't use.
 *
 * Fail-open policy: when `permissions` is `null` (before the login response
 * lands, or during the brief session-restore reconcile) these helpers grant
 * access rather than deny it. The value arrives synchronously in the login
 * response and is cached across reloads, so `null` is a rare transient — and
 * because the server is the real boundary, failing open here can never grant
 * real capability, only avoid flashing the operator's own controls off.
 *
 * @module permissions
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPermissions = getPermissions;
exports.maxBetUsd = maxBetUsd;
exports.isWithinBetCap = isWithinBetCap;
exports.canPlaceBets = canPlaceBets;
exports.clampBetToCap = clampBetToCap;
exports.hasFeature = hasFeature;
exports.canManualPrice = canManualPrice;
exports.permsAllowCloseOpposite = permsAllowCloseOpposite;
exports.permsAllowSellOrders = permsAllowSellOrders;
exports.canSellOrders = canSellOrders;
exports.canCloseOpposite = canCloseOpposite;
exports.permsAllowAdditionalMarkets = permsAllowAdditionalMarkets;
exports.canAdditionalMarkets = canAdditionalMarkets;
exports.permsAllowParallelMarkets = permsAllowParallelMarkets;
exports.canParallelMarkets = canParallelMarkets;
exports.canNovigVenue = canNovigVenue;
exports.canArbMonitor = canArbMonitor;
exports.permsAllowDelayedPrice = permsAllowDelayedPrice;
exports.canDelayedPrice = canDelayedPrice;
exports.canToggleConfirm = canToggleConfirm;
exports.canPriceSample = canPriceSample;
exports.canPnlAllUsers = canPnlAllUsers;
exports.canConnectionCheck = canConnectionCheck;
exports.canWinProb = canWinProb;
exports.currentLevel = currentLevel;
exports.formatBetCap = formatBetCap;
const state_1 = require("./state");
/** The `features` sentinel that grants every feature (owner tier). */
const ALL_FEATURES = "*";
/** Current permission set, or `null` if not yet captured/hydrated. */
function getPermissions() {
    return (0, state_1.getState)().permissions;
}
/**
 * Per-order bet cap in USD. `null` on the wire (uncapped), a missing permission
 * set, and an absent cap field all resolve to `Infinity` so callers can compare
 * numerically without special-casing. The nullish check (not `=== null`) keeps
 * a partial permission object failing open — never silently to `undefined`,
 * which would make `> 0` comparisons (e.g. `canPlaceBets`) read as a $0 cap.
 */
function maxBetUsd() {
    const perms = getPermissions();
    if (!perms || perms.maxBetUsd == null)
        return Infinity;
    return perms.maxBetUsd;
}
/** Whether a per-order notional (USD) is at or under the cap. */
function isWithinBetCap(notionalUsd) {
    return notionalUsd <= maxBetUsd();
}
/**
 * Whether the current tier may place bets at all. A $0 cap (level 0) can't —
 * the server rejects every order from such a tier — so the client blocks the
 * buy up front rather than send a doomed order. Fails open when permissions are
 * absent (`maxBetUsd()` → `Infinity`), consistent with the module header.
 * Mirrors the server's `canPlaceBets`.
 */
function canPlaceBets() {
    return maxBetUsd() > 0;
}
/**
 * Clamp a requested bet notional to the cap. Mirrors the server's
 * `exceedsBetLimit` boundary so the committed amount can never exceed the
 * tier's `maxBetUsd`. Non-finite / negative inputs pass through untouched
 * (callers validate those separately).
 */
function clampBetToCap(notionalUsd) {
    const cap = maxBetUsd();
    if (!Number.isFinite(notionalUsd))
        return notionalUsd;
    return Math.min(notionalUsd, cap);
}
/**
 * Whether the current tier grants a named feature. Fails open when
 * permissions are absent; honours the `"*"` owner wildcard. Mirrors the
 * server's `hasFeature`.
 */
function hasFeature(feature) {
    const perms = getPermissions();
    if (!perms)
        return true; // fail open — see module header
    return (perms.features.includes(ALL_FEATURES) ||
        perms.features.includes(feature));
}
/** Whether the manual limit-price control should be available. */
function canManualPrice() {
    const perms = getPermissions();
    if (!perms)
        return true; // fail open — see module header
    return perms.allowManualPrice;
}
/**
 * Whether the "sell opposite" (XOP) close-opposite-on-buy behaviour is
 * available. Server-enforced on the order path; owner-tier only. Pure form
 * (takes the perms) so hot-path callers can pass `appState.permissions`
 * without a second `getState()`.
 *
 * Requires `allowSellOrders` too — it is only the implicit trigger for a sell.
 * Mirrors the server's `canCloseOppositeOnBuy`.
 */
function permsAllowCloseOpposite(perms) {
    if (!perms)
        return true; // fail open — see module header
    return perms.allowCloseOpposite && perms.allowSellOrders;
}
/**
 * Whether SELL orders are available (trusted tiers, 4+). Server-enforced.
 * A cached permission blob predating this field parses to `false` (auth.ts
 * coerces with `=== true`), hiding sells until the session reconcile lands.
 */
function permsAllowSellOrders(perms) {
    if (!perms)
        return true; // fail open — see module header
    return perms.allowSellOrders;
}
/** Whether the current tier may place sell orders. */
function canSellOrders() {
    return permsAllowSellOrders(getPermissions());
}
/** Whether the current tier may close-opposite-on-buy (XOP). */
function canCloseOpposite() {
    return permsAllowCloseOpposite(getPermissions());
}
/**
 * Whether the additional-markets feature (assign + fan out extra orders on a
 * button click) is available. Server-enforced: the order routes reject a tagged
 * additional-market order (403) when the tier lacks it. This mirrors that so
 * the client hides the assign UI and suppresses the fan-out. Pure form (takes
 * the perms) so hot-path order-creation callers can pass `appState.permissions`
 * without a second `getState()`. Fails open on `null` — see module header.
 */
function permsAllowAdditionalMarkets(perms) {
    if (!perms)
        return true; // fail open — see module header
    return perms.allowAdditionalMarkets;
}
/** Whether the current tier may use the additional-markets feature. */
function canAdditionalMarkets() {
    return permsAllowAdditionalMarkets(getPermissions());
}
/**
 * Whether the parallel-markets feature (fan out buy-side parallel orders on a
 * buy) is available. Server-enforced: the order routes reject a tagged parallel
 * order (403) when the tier lacks it. This mirrors that so the client hides the
 * assign UI and suppresses the fan-out. Pure form (takes the perms) so hot-path
 * order-placement callers can pass `appState.permissions` without a second
 * `getState()`. Fails open on `null` — see module header.
 */
function permsAllowParallelMarkets(perms) {
    if (!perms)
        return true; // fail open — see module header
    return perms.allowParallelMarkets;
}
/** Whether the current tier may use the parallel-markets feature. */
function canParallelMarkets() {
    return permsAllowParallelMarkets(getPermissions());
}
/**
 * Whether the Novig venue is offered in the venue selector.
 *
 * UI-only, and the outer of two independent gates: the server mounts the
 * `/novig` router solely when it holds Novig credentials, so a deploy without
 * them exposes nothing regardless of this flag. On no tier today (owner-only
 * via `"*"`) while the venue is built out.
 */
function canNovigVenue() {
    return hasFeature("novig-venue");
}
/**
 * Whether the arbitrage-monitor entry points should be surfaced. UI-only:
 * server access stays gated by the elevated (Y-session) role regardless
 * (see PERMISSION_LEVELS_PLAN.md §6).
 */
function canArbMonitor() {
    return hasFeature("arb-monitor");
}
/**
 * Whether the T0/Delayed pricing toggle is available (`delayed-price` feature,
 * trusted tiers only). Server-enforced on the order path (the trade + PM-US
 * routes force `useDelayedPrice` off when the tier lacks it); this mirrors that
 * so the client hides the toggle and forces the flag off before sending. Pure
 * form (takes the perms) so hot-path order-creation callers can pass
 * `appState.permissions` without a second `getState()`. Fails open on `null` —
 * see module header.
 */
function permsAllowDelayedPrice(perms) {
    if (!perms)
        return true; // fail open — see module header
    return (perms.features.includes(ALL_FEATURES) ||
        perms.features.includes("delayed-price"));
}
/** Whether the current tier may use the delayed-price toggle. */
function canDelayedPrice() {
    return permsAllowDelayedPrice(getPermissions());
}
/**
 * Whether the two-click (2C) confirmation toggle should be available
 * (`confirm-toggle` feature, trusted tiers only — level 4+). UI-only: the
 * 2-click guard is a purely client-side safety with no server side, so this
 * only governs whether the operator may relax it. Tiers without the flag keep
 * the guard on (the confirm flag defaults on) and never see the toggle. Fails
 * open on `null` — see module header.
 */
function canToggleConfirm() {
    return hasFeature("confirm-toggle");
}
/**
 * Whether the MARK button (the operator's own price sample on a filled order)
 * should be surfaced (`price-sample` feature, trusted tiers only — level 4+).
 * UI-only: the endpoint scopes every leg to the caller's own click id, so a
 * tier without the flag has nothing to reach for anyway — the gate just keeps
 * a control they'd never arm off the order page. Fails open on `null` — see
 * module header.
 */
function canPriceSample() {
    return hasFeature("price-sample");
}
/**
 * Whether to offer the PnL page's cross-user controls — Monthly Me/All and
 * "view as" (`pnl-all-users`; only the owner's `"*"` grants it). UI-only: the
 * /pnl routes honour `scope=all` and `viewAs` for the owner alone.
 */
function canPnlAllUsers() {
    return hasFeature("pnl-all-users");
}
/**
 * Whether the operator may use the settings-modal connection check (the
 * network diagnostic). Listed on no tier server-side, so only the owner's
 * `"*"` wildcard grants it. Fails open on `null` — see module header — which
 * is harmless here: the control only probes the unauthenticated /health
 * endpoint, and the gate re-evaluates as soon as permissions land.
 */
function canConnectionCheck() {
    return hasFeature("connection-check");
}
/**
 * Whether the win-probability page is available (owner only; listed on no tier
 * server-side, so only the `"*"` wildcard grants it). Fails open on `null` like
 * every helper here, which is safe because the `/win-prob` router enforces the
 * same flag: a prematurely shown button gets a 403, not data.
 */
function canWinProb() {
    return hasFeature("win-prob");
}
/** The current level number, or `null` when unknown. */
function currentLevel() {
    return getPermissions()?.level ?? null;
}
/**
 * Human-readable cap for display, e.g. `"$100"` or `"unlimited"`.
 * `null`/absent cap → `"unlimited"`.
 */
function formatBetCap() {
    const cap = maxBetUsd();
    return Number.isFinite(cap) ? `$${cap}` : "♾️";
}
